import { apiClient } from '@/core/api/client';
import type { V2TakePayload } from './v2TakeApi';

/** Feature 095 — anonymous client take flow (public, token-identified). */

export type ResponseOption = { value: 0 | 1 | 2; label: string; description: string };
export type AssessmentQuestion = { key: string; text: string };
export type AssessmentPillar = {
  key: string;
  num: number;
  name: string;
  /** Core Question — framing shown on the pillar screen. */
  framing: string;
  /** One-line plain-language summary shown under the Core Question. */
  blurb: string;
  questions: AssessmentQuestion[];
};
export type FreeTextPrompt = { key: string; prompt: string; required: boolean };

export type AssessmentContent = {
  pillars: AssessmentPillar[];
  responseOptions: ResponseOption[];
  vision: FreeTextPrompt;
  concern: FreeTextPrompt;
  version: string;
};

export type TakePayload = {
  /** The report routes take this, not the token. */
  assessmentId: string;
  /**
   * The advisor is taking this themselves (Feature 118). The completion screen
   * then frames the share step as a preview of what the client is asked, and
   * offers the report instead of account creation — the advisor already has one.
   */
  isSelfTest: boolean;
  clientName: string;
  clientEmail: string;
  /** Whether this email already has a bQuest account (drives sign-in vs create CTA). */
  hasAccount: boolean;
  advisorName: string;
  status: 'INVITED' | 'IN_PROGRESS' | 'COMPLETED';
  isExpired: boolean;
  content: AssessmentContent;
  savedAnswers: Record<string, number>;
  savedVision: string | null;
  savedConcern: string | null;
};

export type SaveProgressBody = {
  answers?: { questionKey: string; value: 0 | 1 | 2 }[];
  vision?: string;
  concern?: string;
};

/**
 * One route, two question sets: the backend answers with the version the
 * assessment was stamped with, so a link sent before MVP 2 still opens the V1
 * questions. Narrow the result with `isV2Payload` before reading `content`.
 */
export async function getAssessmentByToken(token: string): Promise<TakePayload | V2TakePayload> {
  const response = await apiClient.get<TakePayload | V2TakePayload>(
    `/longevity-assessments/take/${encodeURIComponent(token)}`,
  );
  return response.data;
}

export async function saveAssessmentProgress(
  token: string,
  body: SaveProgressBody,
): Promise<{ status: TakePayload['status'] }> {
  const response = await apiClient.patch<{ status: TakePayload['status'] }>(
    `/longevity-assessments/take/${encodeURIComponent(token)}`,
    body,
  );
  return response.data;
}

export type ShareStatus = 'SHARED' | 'NONE';

export type CompletionResult = {
  overallScore: number;
  category: string;
  shareStatus: ShareStatus;
};

/** Finish the assessment with the client's sharing choice; scores + completes it. */
export async function completeAssessment(
  token: string,
  shareStatus: ShareStatus,
): Promise<CompletionResult> {
  const response = await apiClient.post<CompletionResult>(
    `/longevity-assessments/take/${encodeURIComponent(token)}/complete`,
    { shareStatus },
  );
  return response.data;
}
